/**
 * `<caos-chart>` — a set of figures drawn as bars or a line, so a trend can be seen rather than read.
 * (Catalog: Data display › Chart — a set of figures drawn as bars or a line, so a trend can be seen rather than read.)
 *
 * ── WHAT IT DRAWS, AND WHY THOSE THREE ───────────────────────────────────────
 * `bar` — a bar per category, scaled against the largest figure in the set.
 * `line` — a value over time, with a dot at each reading.
 * `sparkline` — the same line with its caption, axis and dots stripped, for use INSIDE another
 * piece. StatTile ships a `visual` slot whose own documentation asks for "a sparkline or similar"
 * and says the library has nothing to put there; this is the thing it was waiting for.
 *
 * The three were not picked for being easy. Each is asked for by name somewhere in this repository:
 * AppHome hand-draws the bar (a label, a figure, a track and an accent fill) because the library
 * had none, the ticket's own words ask for "a number moving over time", and StatTile's slot asks
 * for the sparkline. An area fill would be a variant of the line rather than a fourth type.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DRAW ───────────────────────────────────────
 * No pie, no donut, and no second series. All three need a CATEGORICAL COLOUR AXIS — a set of
 * series colours that are distinguishable from one another and mean nothing in particular. No such
 * axis exists: the design system's token source and the compiled token floor the workspace loads
 * both have only the semantic families (accent, success, warning, danger, info) and the neutrals.
 * Inventing one here would settle a design-system question that CAOS-586 owns. So this piece draws
 * ONE series, which needs no such axis, and the gap is named rather than worked around.
 *
 * That is also why it is readable without colour, by construction rather than by care: with a
 * single series, colour carries no information at all, so a reader who cannot separate two hues
 * loses nothing. Every figure is drawn beside its own label, in text.
 *
 * ── IT TAKES DATA, NOT A REPORT ──────────────────────────────────────────────
 * `points` is a plain list handed to the element. There is no saved report, no query, and no
 * binding to a reporting layer — the incumbent couples those permanently and this does not.
 * CAOS-286 carries reports and dashboards as a metadata type; when it lands it becomes one more
 * caller of this, never a precondition for it.
 *
 * ── NO MEASUREMENT ───────────────────────────────────────────────────────────
 * The line is drawn into a FIXED coordinate space and scaled by the browser, so nothing here reads
 * `getBoundingClientRect` or `getBBox`. A chart that measured itself would draw nothing under a DOM
 * with no layout engine, which is what the suite runs on — it would be untestable exactly where it
 * needed testing. All SVG is built through the DOM, never `innerHTML`, so a strict CSP is satisfied.
 *
 * Figures are drawn as given. Like StatTile, this piece rounds, groups and abbreviates nothing: a
 * point may carry `display` with the text to show, which keeps formatting where the caller's
 * locale and currency are known.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** How the figures are drawn. Anything unrecognised reads as `bar`. */
export type CaosChartType = 'bar' | 'line' | 'sparkline';

/** One reading: what it is, what it was, and optionally how to write it down. */
export interface CaosChartPoint {
  /** What this reading is — a category for a bar, a moment for a line. */
  readonly label?: string;
  /** The figure itself. Must be finite; anything else is dropped rather than drawn as zero. */
  readonly value: number;
  /** The figure already formatted. Absent means the number is written out as it stands. */
  readonly display?: string;
}

/** Shown when there is nothing to draw. Saying so plainly beats an empty chart reading as zero. */
const DEFAULT_EMPTY = 'Nothing to show yet.';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The piece's own sheet, as TEXT. Exported so its tests can read the rules themselves rather than
 * only through the adopted sheet — a DOM with no layout engine can drop a declaration it cannot
 * evaluate, and a token read only there would vanish from `cssRules` and narrow a completeness
 * check without saying so.
 */
export const CHART_STYLES = `
  :host { display: block; }

  .chart { display: flex; flex-direction: column; gap: var(--caos-space-2); font-family: var(--caos-font); }

  .caption { font-size: 12.5px; font-weight: 500; color: var(--caos-color-text); }

  .empty { font-size: 12.5px; color: var(--caos-color-text-muted); }

  .rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--caos-space-2); }
  .row__head { display: flex; justify-content: space-between; align-items: baseline; gap: var(--caos-space-3); font-size: 12px; margin-bottom: 4px; }
  .row__label { color: var(--caos-color-text); }
  .row__value { color: var(--caos-color-text); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .row__track { height: 7px; border-radius: 999px; background: var(--caos-color-bg); overflow: hidden; }
  .row__fill { height: 100%; border-radius: 999px; background: var(--caos-color-accent); }

  .figure { display: block; width: 100%; height: auto; }
  .line { fill: none; stroke: var(--caos-color-accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  .dot { fill: var(--caos-color-accent); }

  .axis { display: flex; justify-content: space-between; font-size: 11px; color: var(--caos-color-text-muted); }

  /* A sparkline sits inside somebody else's box — no caption, no axis, and no gap to push them apart. */
  :host([type="sparkline"]) .chart { gap: 0; }

  /* The text a drawing cannot carry. Present for assistive technology, absent from the picture. */
  .sr {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

const sheet = makeSheet(CHART_STYLES);

/** The coordinate space a full line is drawn in — fixed, so nothing has to be measured. */
const LINE_BOX = { w: 300, h: 96, pad: 6 };
/** The same for a sparkline, which is wider than it is tall because it sits on one line of text. */
const SPARK_BOX = { w: 100, h: 24, pad: 3 };

type Box = { w: number; h: number; pad: number };

function svgNode(tag: string, attrs: Record<string, string>): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

/** Two decimals is plenty in a 300-unit box, and it keeps the emitted path readable. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Normalise one entry. Returns null for anything that is not a finite figure. */
function toPoint(entry: unknown, index: number): CaosChartPoint | null {
  const fallbackLabel = String(index + 1);
  if (typeof entry === 'number') {
    return Number.isFinite(entry) ? { label: fallbackLabel, value: entry } : null;
  }
  if (entry === null || typeof entry !== 'object') return null;
  const e = entry as Record<string, unknown>;
  const raw = e['value'];
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  const label = typeof e['label'] === 'string' && e['label'].length > 0 ? e['label'] : fallbackLabel;
  const display = typeof e['display'] === 'string' ? e['display'] : undefined;
  return display === undefined ? { label, value: raw } : { label, value: raw, display };
}

/**
 * How wide one bar is drawn, as a percentage.
 *
 * AppHome floors every bar at 2% so a small figure is still visible, and that floor is kept — but
 * only for a figure that is actually there. A ZERO is drawn as nothing, because a visible bar for a
 * figure of nought states something untrue, and the same goes for a negative on a scale that
 * starts at nought.
 */
function barWidth(value: number, top: number): number {
  if (top <= 0 || value <= 0) return 0;
  return Math.min(100, Math.max(2, Math.round((value / top) * 100)));
}

/** The polyline, as `x,y` pairs in the given box. */
function polyPoints(values: readonly number[], box: Box, lo: number, hi: number): string {
  const x0 = box.pad;
  const x1 = box.w - box.pad;
  const y0 = box.pad;
  const y1 = box.h - box.pad;
  const n = values.length;
  return values
    .map((v, i) => {
      // One reading has no span to sit along, and a flat set has no span to rise through. Both go
      // to the middle of the axis they cannot use, which reads as "no movement" rather than "nought".
      const x = n === 1 ? (x0 + x1) / 2 : x0 + (i / (n - 1)) * (x1 - x0);
      // A `max` the page pinned can sit BELOW the largest reading. Left alone, the ratio goes over
      // one and the point lands outside the box, where the browser clips it — the highest reading
      // would disappear from the picture instead of being capped. Bars already stop at 100%; this is
      // that same ceiling, and the same floor for a `max` pinned under the whole set.
      const raw = hi === lo ? (y0 + y1) / 2 : y1 - ((v - lo) / (hi - lo)) * (y1 - y0);
      const y = Math.min(y1, Math.max(y0, raw));
      return `${round(x)},${round(y)}`;
    })
    .join(' ');
}

export class CaosChart extends CaosElement {
  static get observedAttributes(): string[] {
    return ['type', 'label', 'max', 'empty-label'];
  }

  private pointList: CaosChartPoint[] = [];
  private readonly wrap: HTMLElement;

  constructor() {
    super(sheet);
    this.wrap = document.createElement('div');
    this.wrap.className = 'chart';
    this.wrap.setAttribute('part', 'chart');
    this.root.append(this.wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** The readings, in order. Set as a property: a list cannot ride an attribute. */
  get points(): CaosChartPoint[] {
    return this.pointList.map((p) => ({ ...p }));
  }

  set points(list: ReadonlyArray<number | CaosChartPoint>) {
    this.pointList = Array.isArray(list)
      ? list.map((e, i) => toPoint(e, i)).filter((p): p is CaosChartPoint => p !== null)
      : [];
    this.render();
  }

  /** Which drawing. Reflected, so it can be read back off the element. */
  get type(): CaosChartType {
    const raw = this.getAttribute('type');
    return raw === 'line' || raw === 'sparkline' ? raw : 'bar';
  }

  set type(value: CaosChartType) {
    this.setAttribute('type', value);
  }

  /** What the whole set is. Drawn as a caption, and the heading of the text equivalent. */
  get label(): string {
    return this.getAttribute('label') ?? '';
  }

  set label(value: string) {
    if (value) this.setAttribute('label', value);
    else this.removeAttribute('label');
  }

  /** The top of the scale. Absent means the largest figure in the set is the top. */
  get max(): number | null {
    const raw = this.getAttribute('max');
    if (raw === null || raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  set max(value: number | null) {
    if (value === null || !Number.isFinite(value)) this.removeAttribute('max');
    else this.setAttribute('max', String(value));
  }

  /** What is said when there is nothing to draw. */
  get emptyLabel(): string {
    return this.getAttribute('empty-label') ?? DEFAULT_EMPTY;
  }

  set emptyLabel(value: string) {
    if (value) this.setAttribute('empty-label', value);
    else this.removeAttribute('empty-label');
  }

  private render(): void {
    const type = this.type;
    const label = this.label;
    const drawn: Node[] = [];

    // A sparkline sits inside another piece, which has already said what the figures are.
    if (label !== '' && type !== 'sparkline') {
      const caption = document.createElement('div');
      caption.className = 'caption';
      caption.setAttribute('part', 'caption');
      caption.textContent = label;
      drawn.push(caption);
    }

    if (this.pointList.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.setAttribute('part', 'empty');
      empty.textContent = this.emptyLabel;
      drawn.push(empty);
      this.wrap.replaceChildren(...drawn);
      return;
    }

    if (type === 'bar') drawn.push(this.drawBars());
    else drawn.push(...this.drawLine(type));

    this.wrap.replaceChildren(...drawn);
  }

  /** Bars are ELEMENTS, not a picture: the label and the figure are already text. */
  private drawBars(): HTMLElement {
    const list = document.createElement('ol');
    list.className = 'rows';
    list.setAttribute('part', 'rows');
    const top = this.max ?? Math.max(...this.pointList.map((p) => p.value), 0);

    for (const point of this.pointList) {
      const row = document.createElement('li');
      row.setAttribute('part', 'row');

      const head = document.createElement('div');
      head.className = 'row__head';

      const label = document.createElement('span');
      label.className = 'row__label';
      label.setAttribute('part', 'label');
      label.textContent = point.label ?? '';

      const value = document.createElement('span');
      value.className = 'row__value';
      value.setAttribute('part', 'value');
      value.textContent = point.display ?? String(point.value);

      head.append(label, value);

      const track = document.createElement('div');
      track.className = 'row__track';
      track.setAttribute('part', 'track');

      const fill = document.createElement('div');
      fill.className = 'row__fill';
      fill.setAttribute('part', 'fill');
      fill.style.width = `${barWidth(point.value, top)}%`;
      track.append(fill);

      row.append(head, track);
      list.append(row);
    }

    return list;
  }

  /** A line is a picture, so it is hidden from assistive technology and given a text equivalent. */
  private drawLine(type: CaosChartType): Node[] {
    const box = type === 'sparkline' ? SPARK_BOX : LINE_BOX;
    const values = this.pointList.map((p) => p.value);
    const lo = Math.min(...values);
    const hi = this.max ?? Math.max(...values);

    const svg = svgNode('svg', {
      viewBox: `0 0 ${box.w} ${box.h}`,
      'aria-hidden': 'true',
      focusable: 'false',
      class: 'figure',
      part: 'figure',
    });

    const pairs = polyPoints(values, box, lo, hi);
    svg.append(svgNode('polyline', { points: pairs, class: 'line', part: 'line' }));

    // A dot per reading says where a reading actually IS, rather than leaving the eye to guess at
    // the bends. The sparkline goes without: at that size they close up into a thicker line.
    if (type === 'line') {
      for (const pair of pairs.split(' ')) {
        const [cx, cy] = pair.split(',');
        svg.append(svgNode('circle', { cx: cx ?? '0', cy: cy ?? '0', r: '2.5', class: 'dot', part: 'point' }));
      }
    }

    const out: Node[] = [svg];
    if (type === 'line') out.push(this.axis());
    out.push(this.dataTable());
    return out;
  }

  /** The ends of the run, written out, so the line is anchored to something readable. */
  private axis(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'axis';
    row.setAttribute('part', 'axis');

    const first = document.createElement('span');
    first.textContent = this.pointList[0]?.label ?? '';
    const last = document.createElement('span');
    last.textContent = this.pointList[this.pointList.length - 1]?.label ?? '';

    row.append(first, last);
    return row;
  }

  /**
   * The figures as a table, present for a reader and absent from the picture.
   *
   * Only the drawn types get one. Bars are already text on the page, and a second copy would have a
   * screen reader say every figure twice.
   */
  private dataTable(): HTMLElement {
    const table = document.createElement('table');
    table.className = 'sr';
    table.setAttribute('part', 'table');

    const caption = document.createElement('caption');
    caption.textContent = this.label !== '' ? this.label : 'Chart data';
    table.append(caption);

    const body = document.createElement('tbody');
    for (const point of this.pointList) {
      const row = document.createElement('tr');
      const head = document.createElement('th');
      head.setAttribute('scope', 'row');
      head.textContent = point.label ?? '';
      const cell = document.createElement('td');
      cell.textContent = point.display ?? String(point.value);
      row.append(head, cell);
      body.append(row);
    }
    table.append(body);

    return table;
  }
}

define('caos-chart', CaosChart);
